import React from 'react';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import {
  Icon,
  Typography,
  Button,
  LinearProgress,
  Box,
} from '@mui/material';
import { DynamicTable, TitleTooltip } from 'components';
import { withRouter} from 'utils';
import { DateUtil } from 'doctivity-shared/utils';
import { queryCustomServiceLines, listClaimsServicelines, } from 'store/actions/claimsActions';
import { CustomServiceLineDialog } from 'components';
import axiosInstance from 'utils/axiosUtil';
import moment from 'moment';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
  },
  spacer: {
    flex: 1,
  },
  addButton: {
    textTransform: 'none',
  },
  progressContainer: {
    maxWidth: 180,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    marginTop: 4,
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 11,
  },
  completeText: {
    color: theme.palette.primary.main,
    fontSize: 12,
  },
  notProcessedText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
});

class CustomServiceLinesPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dialogOpen: false,
      selectedServiceLine: null,
      progress: {},
      query: {
        attributes: [
          'id',
          'name',
          'last_edited',
          'last_edited_by',
          'is_diagnosis',
          'start_processing_date',
          'end_processing_date'
        ],
        include: [
          {
            association: 'edited_by_user',
            attributes: ['id', 'first_name', 'last_name'],
            required: false,
          },
          {
            association: 'codes',
            attributes: ['id', 'code_type', 'code','description'],
            required: false,
          },
        ],
        where: {
          client_id: props.clientId,
        },
        limit: 50,
        offset: 0,
        order: [['last_edited', 'DESC']],
      },
    };
  }

  componentDidMount() {
    this.fetchServiceLines();
  }

  componentDidUpdate(prevProps) {
    if (this.props.clientId !== prevProps.clientId) {
      const { query } = this.state;
      query.where.client_id = this.props.clientId;
      this.setState({ query }, this.fetchServiceLines);
    }
    
    // Fetch progress when servicelines change
    if (this.props.customServiceLines !== prevProps.customServiceLines) {
      this.fetchProgress();
    }
  }

  fetchServiceLines = () => {
    const { query } = this.state;
    query.where.client_id = this.props.clientId;
    this.props.dispatch(queryCustomServiceLines(query));
  };

  fetchProgress = async () => {
    const { customServiceLines } = this.props;
    const rows = customServiceLines?.data?.rows;
    
    if (!rows || rows.length === 0) {
      this.setState({ progress: {} });
      return;
    }
    
    // Get IDs of servicelines that have been processed or are processing
    const ids = rows
      .filter(sl => sl.start_processing_date)
      .map(sl => sl.id);
    
    if (ids.length === 0) {
      this.setState({ progress: {} });
      return;
    }
    
    try {
      const response = await axiosInstance.get('/ClaimsServicelines', {
        params: { progress: ids.join(',') }
      });
      this.setState({ progress: response.data });
    } catch (err) {
      console.error('Error fetching progress:', err);
    }
  };

  onQueryChange = (query) => {
    query.where.client_id = this.props.clientId;
    this.setState({ query }, this.fetchServiceLines);
  };

  onAddServiceLine = () => {
    this.setState({
      dialogOpen: true,
      selectedServiceLine: null,
    });
  };

  onRowClick = (row) => {
    this.setState({
      dialogOpen: true,
      selectedServiceLine: row,
    });
  };

  onCloseDialog = () => {
    this.setState({
      dialogOpen: false,
      selectedServiceLine: null,
    });
  };

  onSaveDialog = () => {
    this.setState({
      dialogOpen: false,
      selectedServiceLine: null,
    });
    this.fetchServiceLines();
    this.props.dispatch(listClaimsServicelines());
  };

  renderProgressStatus = (row) => {
    const { classes } = this.props;
    const { progress } = this.state;
    const p = progress[row.id];
    
    const isProcessing = row.start_processing_date && !row.end_processing_date;
    const isComplete = row.start_processing_date && row.end_processing_date;
    
    if (isProcessing && p) {
      // Calculate progress using lastEntityId / maxId approach
      let percentage = 0;
      let currentPhase = 'Processing...';
      
      if (p.currentEntityType === 'providers') {
        const maxId = p.providersMaxId || 0;
        percentage = maxId > 0 ? Math.round(((p.lastEntityId || 0) / maxId) * 50) : 0; // 0-50% for providers
        currentPhase = `Providers: ${(p.providersCount || 0).toLocaleString()} processed`;
      } else if (p.currentEntityType === 'organizations') {
        const maxId = p.organizationsMaxId || 0;
        const orgProgress = maxId > 0 ? Math.round(((p.lastEntityId || 0) / maxId) * 50) : 0;
        percentage = 50 + orgProgress; // 50-100% for organizations
        currentPhase = `Orgs: ${(p.organizationsCount || 0).toLocaleString()} processed`;
      }
      
      return (
        <Box className={classes.progressContainer}>
          <div className={classes.progressLabel}>
            <span>{currentPhase}</span>
            <span>~{percentage}%</span>
          </div>
          <LinearProgress
            variant="determinate"
            value={percentage}
            className={classes.progressBar}
          />
        </Box>
      );
    } else if (isProcessing) {
      // Processing but no progress data yet
      return (
        <Box className={classes.progressContainer}>
          <Typography variant="caption" color="textSecondary">
            Started {DateUtil.formatFromNow(row.start_processing_date)}
          </Typography>
          <LinearProgress className={classes.progressBar} />
        </Box>
      );
    } else if (isComplete && p) {
      // Complete with stats
      return (
        <Typography className={classes.completeText}>
          ✓ {(p.providersCount || 0).toLocaleString()} providers, {(p.organizationsCount || 0).toLocaleString()} orgs
        </Typography>
      );
    } else if (isComplete) {
      // Complete without stats
      return (
        <Typography className={classes.completeText}>
          ✓ Complete
        </Typography>
      );
    } else {
      // Not processed
      return (
        <Typography className={classes.notProcessedText}>
          Not processed
        </Typography>
      );
    }
  };

  getColumns = () => {
    return [
      {
        label: 'Name',
        key: 'name',
        style: { width: '35%' },
      },
      {
        label: 'Type',
        key: 'is_diagnosis',
        style: { width: '10%' },
        format: (is_diagnosis) => {
          return (is_diagnosis ? 'Dx' : 'Px');
        }
      },
      {
        label: '# of Codes',
        key: 'code_count',
        style: { width: '8%' },
      },
      {
        label: 'Processing',
        key: 'processing_status',
        style: { width: '20%' },
        format: (value, row) => this.renderProgressStatus(row),
      },
      {
        label: 'Last Edit',
        key: 'last_edited',
        style: { width: '10%' },
        format: (date) => date ? moment(date).format('MM/DD/YYYY') : '',
      },
      {
        label: 'Last Edited by',
        key: 'edited_by_name',
        style: { width: '12%' },
      },
    ];
  };

  render() {
    const { classes, customServiceLines } = this.props;
    const { dialogOpen, selectedServiceLine, query } = this.state;

    // Transform data to include code_count and edited_by_name
    const transformedData = {
      ...customServiceLines,
      data: {
        ...customServiceLines.data,
        rows: customServiceLines.data?.rows?.map(row => {
          return {
            ...row,
            code_count: row.codes?.length || 0,
            edited_by_name: row.edited_by_user
              ? `${row.edited_by_user.last_name}, ${row.edited_by_user.first_name}`
              : '',
          };
        }) || [],
      },
    };

    return (
      <>
        <title>Custom Service Lines</title>
        <div className={classes.container}>

          <DynamicTable
            header={
              <div className={classes.tableHeader}>
                <Typography
                  component='h2'
                  variant='h6'
                  color='primary'
                  gutterBottom
                >
                  Custom Service Lines
                </Typography>
                  <div className={classes.spacer} />
                <Button
                  color='primary'
                  size='small'
                  onClick={this.onAddServiceLine}
                >
                  <Icon className={classes.leftIcon}>add</Icon>
                  Add Custom Service Line
                </Button>
              </div>
            }
            columns={this.getColumns()}
            query={query}
            data={transformedData}
            onRowClick={this.onRowClick}
            onQueryChange={this.onQueryChange}
            allowNew={false}
            showFilters={false}
          />
        </div>
        {dialogOpen && (
          <CustomServiceLineDialog
            open={dialogOpen}
            serviceLine={selectedServiceLine}
            clientId={this.props.clientId}
            userId={this.props.userId}
            onClose={this.onCloseDialog}
            onSave={this.onSaveDialog}
          />
        )}
      </>
    );
  }
}

CustomServiceLinesPage.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  customServiceLines: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    clientId: state.app.selectedClient,
    userId: state.user.id,
    customServiceLines: state.claims?.custom_servicelines || {
      meta: { loadStatus: 'STATUS_NONE' },
      data: { rows: [], count: 0 }
    },
  };
}

const styled = withStyles(styles)(CustomServiceLinesPage);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as CustomServiceLinesPage };